import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartIcon, ArrowLeftIcon, CheckIcon } from '../ui/icons';
import styled, { keyframes } from 'styled-components';
import './WidgetMedia.css';
import DiscountUnitDropdown from '../ui/DiscountUnitDropdown';
import { PanelContainer, Title, Subtitle, PrimaryButton, PageHeader, TitleRow, IconButton } from '../ui/common';
import { useTranslation } from '../translations';
import { API_BASE, WIDGET_BASE } from '../config';
import { wybierzHostWidgetu, adresyWidgetu, czySondowac, linkRezerwacji } from '../utils/widgetHost';
import BookingLinkQr from '../components/widget/BookingLinkQr';
import { Banner } from '../components/ui/Banner';
import { superAdminFirmyBezPremium } from '../utils/planFirmy';
import { scrollToTop } from '../utils/scrollToTop';
import WidgetFiltersPanel from '../components/widget/WidgetFiltersPanel';
import WidgetClassBookingPanel from '../components/widget/WidgetClassBookingPanel';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return '';
};

const normalizeKey = (value) => String(value || '').trim().toLowerCase();

// Rozwinięte grupy kafelków zdjęć klas i kategorii (ZwijanaGrupa).
const KLUCZ_OTWARTYCH_GRUP = 'widgetMediaOpenGroups:v1';

// Palety kolorów WIDGETU osadzanego na stronie klienta — celowo poza tokenami motywu panelu,
// bo to kolory produktu organizacji, nie tego admin-UI.
// Losowe pary do przycisku "Losuj" we własnym motywie — dobrane ręcznie (nie czysty
// losowy RGB), żeby każdy wynik był harmonijny, nigdy błotnisty.
const GRADIENT_SHUFFLE_PAIRS = [
  ['#ff6b6b', '#556270'],
  ['#f857a6', '#ff5858'],
  ['#43cea2', '#185a9d'],
  ['#4568dc', '#b06ab3'],
  ['#0f2027', '#2c5364'],
  ['#f7971e', '#ffd200'],
  ['#8e2de2', '#4a00e0'],
  ['#00c6ff', '#0072ff'],
  ['#ee0979', '#ff6a00'],
  ['#11998e', '#38ef7d'],
];

const COLOR_THEMES = [
  { nameKey: 'widgetMedia.themeDarkBlue', primary: '#2c3e50', hover: '#243645', accent: '#5B84B1' },
  { nameKey: 'widgetMedia.themeBlue', primary: '#0d6efd', hover: '#0b5ed7', accent: '#10b981' },
  { nameKey: 'widgetMedia.themePurple', primary: '#7c3aed', hover: '#6d28d9', accent: '#ec4899' },
  { nameKey: 'widgetMedia.themeGreen', primary: '#059669', hover: '#047857', accent: '#0ea5e9' },
  { nameKey: 'widgetMedia.themeOrange', primary: '#ea580c', hover: '#c2410c', accent: '#facc15' },
  { nameKey: 'widgetMedia.themeRed', primary: '#dc2626', hover: '#b91c1c', accent: '#f97316' },
  { nameKey: 'widgetMedia.themeTurquoise', primary: '#0891b2', hover: '#0e7490', accent: '#8b5cf6' },
  { nameKey: 'widgetMedia.themeOcean', primary: '#764ba2', hover: '#6a4192', accent: '#10b981' },
  { nameKey: 'widgetMedia.themeSunset', primary: '#f5576c', hover: '#e5475c', accent: '#fbbf24' },
  { nameKey: 'widgetMedia.themeNight', primary: '#00f2fe', hover: '#00e2ee', accent: '#a78bfa' },
  { nameKey: 'widgetMedia.themeNature', primary: '#38f9d7', hover: '#28e9c7', accent: '#fb923c' }
];

// ─── Syntetyczny podgląd widgetu ─────────────────────────────────────────────
function getSolidColor(val, fallback) {
  if (!val) return fallback || '#2563eb';
  const m = String(val).match(/#[0-9a-fA-F]{3,8}/);
  return m ? m[0] : (String(val).startsWith('#') ? val : fallback || '#2563eb');
}

const SYNTH_CLASSES = [
  { name: 'Junior' },
  { name: 'Normalny' },
  { name: 'Premium' },
];
const SYNTH_CATEGORIES = [
  { name: 'Narty' },
  { name: 'Buty' },
  { name: 'Kijki' },
  { name: 'Kask' },
];
const SYNTH_PRODUCTS = [
  { id: 1, name: 'Narty Fischer RC4',         brand: 'Fischer',   price_total: 120, price_day: 24, sizes: ['150', '160', '170'] },
  { id: 2, name: 'Narty Rossignol Experience', brand: 'Rossignol', price_total: 100, price_day: 20, sizes: ['155', '165'] },
  { id: 3, name: 'Narty Salomon S/Race',       brand: 'Salomon',   price_total: 110, price_day: 22, sizes: ['160', '170', '180'] },
];

const CheckSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

// P8.1e (4/6): napisy W TYM KOMPONENCIE ZOSTAJĄ PO POLSKU i nie jest to przeoczenie.
// To PODGLĄD widgetu, który wypożyczalnia osadza u siebie na stronie — a jego treść jest
// dosłownym lustrem `widget/src/main.js` („Skompletuj swój zestaw", „Twój zestaw",
// „Popularny wybór", „Rezerwacja online" stoją tam tak samo). Sam widget to OSOBNA
// aplikacja, bez i18n i wdrażana własnym tempem przez klientów.
//
// Gdyby podgląd szedł przez `t()`, pracownik z panelem po niemiecku zobaczyłby niemiecki
// podgląd widgetu, który jego klientom pokazuje polski — podgląd KŁAMAŁBY o tym, co widzi
// klient, a to jego jedyne zadanie. Tłumaczyć go można dopiero RAZEM z `widget/src/main.js`.
// Panel dookoła (pasek podglądu, ustawienia, sekcje obrazów, kod osadzenia) jest przetłumaczony.
function SyntheticWidgetPreview({ settings, mediaMap, classes, types, device }) {
  const [selClass, setSelClass]       = React.useState(null);
  const [selCat,   setSelCat]         = React.useState(null);
  const [selVariants, setSelVariants] = React.useState({});
  const [cart, setCart]               = React.useState({});

  const primaryColor = getSolidColor(settings.primary_color, '#2563eb');
  const isGrad = settings.primary_color && String(settings.primary_color).includes('gradient');
  const sidebarBg = isGrad ? settings.primary_color : primaryColor;

  const pClasses = (classes && classes.length > 0) ? classes.slice(0, 4) : SYNTH_CLASSES;
  const pCats    = (types  && types.length  > 0)  ? types.slice(0, 4)  : SYNTH_CATEGORIES;

  const getImg = (scope, name) => mediaMap ? (mediaMap[`${scope}:${normalizeKey(name)}`] || '') : '';

  const showImages    = settings.show_images !== false;
  const showProgress  = settings.show_progress !== false;
  const showSizeChart = settings.show_size_chart !== false;
  const priceMode     = settings.price_display_mode || 'total';
  const title         = settings.widget_title || 'Wypożyczalnia sprzętu';
  const orgName       = settings.org_name || title;

  const fmtPriceFull = (p) => priceMode === 'per_day'
    ? <><span className="wsp-price-amount">{p.price_day} zł</span><span className="wsp-price-label">/dzień · razem {p.price_total} zł</span></>
    : <><span className="wsp-price-amount">{p.price_total} zł</span><span className="wsp-price-label">({p.price_day} zł/dzień × 5)</span></>;

  const addItem = (id) => setCart(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  const rmItem  = (id) => setCart(prev => { const n = {...prev}; if (n[id] > 1) n[id]--; else delete n[id]; return n; });
  const setSel  = (id, vi) => setSelVariants(prev => ({ ...prev, [id]: vi }));

  const cartItems = SYNTH_PRODUCTS.filter(p => cart[p.id] > 0);
  const cartCount = cartItems.reduce((s, p) => s + (cart[p.id] || 0), 0);
  const cartTotal = cartItems.reduce((s, p) => s + (priceMode === 'per_day' ? p.price_day : p.price_total) * cart[p.id], 0);

  const STEPS = [
    { num: 1, label: 'Termin' },
    { num: 2, label: 'Sprzęt' },
    { num: 3, label: 'Dane i podsumowanie' },
    { num: 4, label: 'Potwierdzenie' },
  ];
  const currentStep = 2;

  return (
    <div className={`wsp-wrapper${device === 'mobile' ? ' wsp-mobile' : ''}`}>

      {/* ── Sidebar ── */}
      <div className="wsp-sidebar" style={{ background: sidebarBg }}>
        <div className="wsp-logo">
          <div className="wsp-logo-icon">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
              <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
          </div>
          <h1 className="wsp-logo-text">{title}</h1>
        </div>
        <div className="wsp-steps">
          {STEPS.map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className={`wsp-step${s.num === currentStep ? ' wsp-step-active' : ''}${s.num < currentStep ? ' wsp-step-done' : ''}`}>
                <div className="wsp-step-num">{s.num < currentStep ? <CheckSvg /> : s.num}</div>
                <div className="wsp-step-text">{s.label}</div>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`wsp-progress-line${s.num < currentStep ? ' wsp-progress-done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="wsp-sidebar-footer">Rezerwacja online · <strong>{orgName}</strong></div>
      </div>

      {/* ── Main — Step 2: Equipment ── */}
      <div className="wsp-main">
        <div className="wsp-container">

          <div className="wsp-trip-strip">
            <span className="wsp-trip-dates">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              01.03.2026 → 05.03.2026 · 5 dni
            </span>
            <a href="#" className="wsp-trip-edit" onClick={e => e.preventDefault()}>Zmień termin</a>
          </div>

          <div className="wsp-header">
            <h1>Skompletuj swój zestaw</h1>
          </div>

          {showProgress && (
            <div className="wsp-mini-stepper">
              <span className={`wsp-mini-step${selCat === null ? ' wsp-mini-active' : ' wsp-mini-done'}`}>
                {selCat !== null ? '✓' : '1.'} Kategoria
              </span>
              <span className="wsp-mini-sep">→</span>
              <span className={`wsp-mini-step${selCat !== null && selClass === null ? ' wsp-mini-active' : selClass !== null ? ' wsp-mini-done' : ''}`}>
                {selClass !== null ? '✓' : '2.'} Klasa sprzętu
              </span>
              <span className="wsp-mini-sep">→</span>
              <span className={`wsp-mini-step${selClass !== null ? ' wsp-mini-active' : ''}`}>3. Rozmiar i ilość</span>
            </div>
          )}

          <div className="wsp-eq-wrapper">
            <div className="wsp-eq-left">
              <div className="wsp-card">

                {/* Category tiles — lustro widget/src/main.js: kategoria (co) przed klasa (jaki
                    poziom), bo pytanie o poziom sprzetu nie ma sensu zanim klient powie, czego
                    w ogole szuka. */}
                <div className="wsp-filter-section">
                  <span className="wsp-filter-label">1 · Kategoria</span>
                  <div className="wsp-tiles-large">
                    {pCats.map((cat, i) => {
                      const img = getImg('category', cat.name);
                      const active = selCat === i;
                      const isPopular = pCats.length >= 3 && i === 1;
                      const initial = String(cat.name || '?').trim().charAt(0).toUpperCase();
                      return (
                        <div key={i}
                          className={`wsp-tile wsp-tile-large${active ? ' wsp-tile-active' : ''}`}
                          style={active ? { borderColor: primaryColor, boxShadow: `0 0 0 2px ${primaryColor}30` } : {}}
                          onClick={() => { setSelCat(i); setSelClass(null); }}>
                          {isPopular && <span className="wsp-tile-flag">Popularny wybór</span>}
                          {showImages
                            ? (img ? <img className="wsp-tile-img" src={img} alt={cat.name} /> : <div className="wsp-tile-thumb">{initial}</div>)
                            : null}
                          <div className="wsp-tile-content">
                            <div className="wsp-tile-label">{cat.name}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Class tiles — shown after category selected */}
                {selCat !== null ? (
                  <div className="wsp-filter-section">
                    <span className="wsp-filter-label">2 · Klasa sprzętu</span>
                    <div className="wsp-tiles-small">
                      {pClasses.map((cls, i) => {
                        const img = getImg('class', cls.name);
                        const active = selClass === i;
                        const initial = String(cls.name || '?').trim().charAt(0).toUpperCase();
                        return (
                          <div key={i}
                            className={`wsp-tile wsp-tile-small${active ? ' wsp-tile-active' : ''}`}
                            style={active ? { borderColor: primaryColor, boxShadow: `0 0 0 2px ${primaryColor}30` } : {}}
                            onClick={() => setSelClass(i)}>
                            {showImages
                              ? (img ? <img className="wsp-tile-img" src={img} alt={cls.name} /> : <div className="wsp-tile-thumb">{initial}</div>)
                              : null}
                            <div className="wsp-tile-content">
                              <div className="wsp-tile-label wsp-tile-label-sm">{cls.name}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="wsp-choose-hint">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span>Zacznij od wyboru kategorii - pokażemy dostępne klasy i rozmiary.</span>
                  </div>
                )}

                {/* Products — shown after class selected */}
                {selClass !== null && (
                  <div className="wsp-products-grid">
                    {SYNTH_PRODUCTS.map(p => {
                      const vi = selVariants[p.id] ?? null;
                      const qty = cart[p.id] || 0;
                      const canInc = vi !== null && qty < 5;
                      const canDec = qty > 0;
                      return (
                        <div key={p.id} className="wsp-product-card">
                          <div className="wsp-product-header">
                            <h3 className="wsp-product-name">{p.name}</h3>
                            <div className="wsp-product-meta">{p.brand}</div>
                          </div>
                          <div className="wsp-variant-selector">
                            <label className="wsp-variant-label">Rozmiar</label>
                            <div className="wsp-variant-buttons">
                              {p.sizes.map((sz, si) => (
                                <button key={si}
                                  className={`wsp-variant-btn${vi === si ? ' wsp-variant-btn-sel' : ''}`}
                                  style={vi === si ? { background: primaryColor, borderColor: primaryColor, color: '#fff' } : {}}
                                  onClick={e => { e.stopPropagation(); setSel(p.id, si); }}>
                                  {sz} cm
                                </button>
                              ))}
                            </div>
                            {vi === null && <div className="wsp-qty-hint">Wybierz rozmiar, aby dodać do koszyka</div>}
                          </div>
                          <div className="wsp-product-price">{fmtPriceFull(p)}</div>
                          <div className="wsp-product-actions">
                            <div className="wsp-product-qty">
                              <div className="wsp-qty-controls">
                                <button className="wsp-qty-btn" onClick={e => { e.stopPropagation(); rmItem(p.id); }} disabled={!canDec}>−</button>
                                <div className="wsp-qty-value">{qty}</div>
                                <button className="wsp-qty-btn" onClick={e => { e.stopPropagation(); addItem(p.id); }} disabled={!canInc}>+</button>
                              </div>
                              <div className="wsp-qty-available">Dostępne: 5{vi === null ? ' (łącznie)' : ''}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Collapsible size chart */}
                {showSizeChart && selCat !== null && (
                  <details className="wsp-size-chart">
                    <summary className="wsp-size-chart-title">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                      </svg>
                      Nie znasz swojego rozmiaru? Sprawdź tabelę
                    </summary>
                    <table className="wsp-size-table">
                      <thead><tr><th>Wzrost (cm)</th><th>Rekomendowany rozmiar</th></tr></thead>
                      <tbody>
                        <tr><td>120-140 cm</td><td>120-130</td></tr>
                        <tr><td>140-155 cm</td><td>130-145</td></tr>
                        <tr><td>155-170 cm</td><td>145-160</td></tr>
                        <tr><td>170-185 cm</td><td>160-175</td></tr>
                      </tbody>
                    </table>
                  </details>
                )}
              </div>
            </div>

            {/* Cart sidebar */}
            <div className="wsp-eq-right">
              <div className="wsp-cart-sidebar">
                <div className="wsp-cart-header">
                  <h2 className="wsp-cart-title">Twój zestaw</h2>
                  {cartCount > 0 && <div className="wsp-cart-badge" style={{ background: primaryColor }}>{cartCount}</div>}
                </div>
                {cartItems.length === 0 ? (
                  <div className="wsp-cart-empty">
                    <div className="wsp-cart-empty-icon"><CartIcon size={32} /></div>
                    <p style={{ fontWeight: 700, color: '#334155' }}>Twój zestaw jest pusty</p>
                    <p style={{ fontSize: '0.84rem', marginTop: 6, color: '#6b7280' }}>Wybierz klasę i kategorię, a potem dodaj sprzęt „+".</p>
                  </div>
                ) : (
                  <>
                    <div className="wsp-cart-items">
                      {cartItems.map(p => (
                        <div key={p.id} className="wsp-cart-item">
                          <div className="wsp-cart-item-info">
                            <div className="wsp-cart-item-name">{p.name}</div>
                            <div className="wsp-cart-item-size">Rozmiar: {p.sizes[selVariants[p.id] ?? 0]}</div>
                          </div>
                          <div className="wsp-cart-item-price">
                            {priceMode === 'per_day' ? `${p.price_day} zł/dzień` : `${p.price_total} zł`}
                          </div>
                          <button className="wsp-cart-item-rm" onClick={e => { e.stopPropagation(); setCart(prev => { const n = {...prev}; delete n[p.id]; return n; }); }}>✕</button>
                        </div>
                      ))}
                    </div>
                    <div className="wsp-cart-total-row">
                      <span className="wsp-cart-total-label">Razem</span>
                      <span className="wsp-cart-total-amt" style={{ color: primaryColor }}>{cartTotal} zł</span>
                    </div>
                  </>
                )}
                <div className="wsp-cart-actions">
                  <button className="wsp-btn-primary"
                    style={isGrad ? { background: sidebarBg } : { background: primaryColor }}
                    disabled={cartItems.length === 0}>
                    Przejdź dalej →
                  </button>
                  {cartItems.length === 0 && (
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center' }}>
                      W następnym kroku dobierzesz akcesoria i podasz dane.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

function WidgetMedia() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [types, setTypes] = useState([]);
  const [media, setMedia] = useState([]);
  // 072: pary kategoria i klasa z magazynu, do zdjęć klasy osobnych w każdej kategorii.
  const [classCombos, setClassCombos] = useState([]);
  const [settings, setSettings] = useState({
    widget_title: '', primary_color: '', primary_hover: '', accent_color: '',
    show_images: true, show_progress: true, price_display_mode: 'total', show_size_chart: true,
    privacy_policy_url: '',
    terms_url: '',
    org_name: ''
  });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  // Kod rozwija się pod przyciskiem, zwykle już poza ekranem (strona ma kilka sekcji nad nim):
  // po rozwinięciu przewijamy do niego, inaczej kliknięcie wygląda, jakby nic nie zrobiło.
  const embedBoxRef = useRef(null);
  useEffect(() => {
    if (!showEmbedCode) return undefined;
    // Natychmiast i po klatce: płynne przewijanie przerywało przeliczenie układu po sondzie hosta.
    const id = requestAnimationFrame(() => embedBoxRef.current?.scrollIntoView({ block: 'nearest' }));
    return () => cancelAnimationFrame(id);
  }, [showEmbedCode]);
  // Widget konta demo nie przyjmuje rezerwacji (regulamin § 6 ust. 3, bramka w
  // POST /widget/reservations). Uprzedzamy przy kodzie do wklejenia, żeby osoba testująca demo nie
  // dowiedziała się o tym dopiero z błędu na swojej stronie. Tylko UX: pilnuje tego serwer.
  const isDemo = localStorage.getItem('isDemo') === '1';
  const [codeCopied, setCodeCopied] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [vpWidth, setVpWidth] = useState(0);
  const vpRef = useRef(null);
  const [embedHost, setEmbedHost] = useState(null);
  const [embedChecking, setEmbedChecking] = useState(true);
  // USUNIETE (P3.33): `awaitingWidgetAck` + `awaitingRef` — oczekiwanie na potwierdzenie z ramki
  // podgladu. Panel renderuje podglad Reactem (`SyntheticWidgetPreview` nizej, przerysowywany
  // kluczem `previewKey`), wiec ramki nie ma i potwierdzenie nie mialo skad przyjsc.
  const [widgetToken, setWidgetToken] = useState('');
  const [widgetAllowedOrigins, setWidgetAllowedOrigins] = useState('');
  // Plan firmy z GET /api/widget-settings, bez wyjątku super-admina. Super-admin widzi ten ekran
  // także na firmie Basic, a publiczny widget takiej firmy odmawia (lib/widgetOrgAccess.js).
  const [planFirmy, setPlanFirmy] = useState(null);
  const [rotatingToken, setRotatingToken] = useState(false);

  const apiBase = useMemo(() => {
    try {
      const host = window.location.hostname;
      if (host && host !== 'localhost' && host !== '127.0.0.1') return window.location.origin;
    } catch (e) {}
    return API_BASE;
  }, []);

  const [isAdmin, setIsAdmin] = useState(
    localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1'
  );
  const [organizationId, setOrganizationId] = useState(localStorage.getItem('organizationId') || '');

  useEffect(() => {
    let mounted = true;
    const syncWhoami = async () => {
      try {
        const res = await fetch(`${apiBase}/api/whoami`, { credentials: 'include' });
        if (!mounted || !res.ok) return;
        const j = await res.json().catch(() => ({}));
        if (!mounted) return;
        if (j.userId) {
          const serverIsAdmin = !!(j.user && j.user.isAdmin);
          const serverIsSuper = !!(j.user && j.user.isSuperAdmin);
          const serverOrg = j.user && j.user.organizationId ? String(j.user.organizationId) : '';
          setIsAdmin(serverIsAdmin || serverIsSuper);
          setOrganizationId(serverOrg);
          localStorage.setItem('isAdmin', serverIsAdmin || serverIsSuper ? '1' : '0');
          localStorage.setItem('isSuperAdmin', serverIsSuper ? '1' : '0');
          localStorage.setItem('organizationId', serverOrg);
          if (j.csrfToken) localStorage.setItem('csrfToken', j.csrfToken);
        }
      } catch (e) {}
    };
    if (!organizationId || !isAdmin) syncWhoami();
    return () => { mounted = false; };
  }, []);

  // USUNIETE (P3.33): nasluch `rentager.widget.settings_applied`. Ten komunikat wysyla widget do
  // `window.parent` (widget/src/main.js), czyli dziala TYLKO gdy podglad jest ramka. W panelu ramki
  // nie ma od dawna, wiec nasluch nigdy nic nie odbieral. Widget swojej polowy nie traci — przyda
  // sie, gdyby podglad kiedys wrocil jako ramka.

  // Jeden punkt prawdy o tym, gdzie stoi widget: konfiguracja wdrozenia, potem sonda (dev),
  // na koncu domena aplikacji. Regula i jej uzasadnienie w utils/widgetHost.js.
  const hostWidgetu = wybierzHostWidgetu({ skonfigurowany: WIDGET_BASE, wykryty: embedHost, apiBase });
  const widgetScriptSrc = adresyWidgetu(hostWidgetu, apiBase).scriptSrc;
  const widgetIdentifier = widgetToken || organizationId || '';
  // F.4: link do strony widgetu otwartej wprost i kod QR. Dopiero po ustaleniu hosta i wczytaniu
  // ustawień (kodu widgetu): wcześniej wyszedłby adres z identyfikatorem zastępczym albo z hostem
  // sondy, który za chwilę się zmieni. WYŁĄCZNIE kod widgetu, bez zapasu na numer firmy: publiczny
  // widget nie przyjmuje numeru firmy jako tokenu (401), a wydrukowanego kodu QR nie da się potem
  // wymienić. Bez kodu (nieudany odczyt ustawień) karta mówi, że linku nie ma.
  const linkDoRezerwacji = (loading || embedChecking)
    ? ''
    : linkRezerwacji({ host: hostWidgetu, apiBase, identyfikator: widgetToken });
  const superAdminBezPremium = superAdminFirmyBezPremium(planFirmy);

  // BUG (znaleziony przy zgloszeniu 401 z produkcji): `token` byl na sztywno wpisany jako
  // `'dev-widget-token'` — to WYLACZNIE lokalny fallback backendu, gdy zmienna srodowiskowa
  // `WIDGET_TOKEN` nie jest ustawiona (server.js: `WIDGET_TOKEN = process.env.WIDGET_TOKEN ||
  // 'dev-widget-token'`), jawnie odrzucany na produkcji jako "slaby token" (lib/validateEnv.js).
  // W DEV ta wartosc przypadkiem dziala: naglowek `X-Widget-Token` rowna sie masterowi
  // backendu, wiec organizacje rozstrzyga dopiero `shop=` w URL. Na PRODUKCJI, gdzie
  // `WIDGET_TOKEN` jest ustawiony na prawdziwy sekret, `'dev-widget-token'` nie jest juz
  // masterem — kod probuje wtedy znalezc organizacje, ktorej `widget_token` DOSLOWNIE rowna
  // sie stringowi "dev-widget-token", taka nie istnieje, wiec kazde zadanie z tego
  // wklejonego kodu dostaje 401. Poprawka: `token` niesie TEN SAM sekret co `widget`/`shop`
  // (prawdziwy `widgetToken` organizacji) — wtedy dziala niezaleznie od tego, czy backend
  // ma ustawiony wlasny WIDGET_TOKEN, czy nie.
  const embedCode = `<!-- Widget rezerwacji - wklej w miejscu, gdzie ma się wyświetlić -->
<div id="rentager-widget"></div>
<script>
  window.RENTAGER_WIDGET_CONFIG = {
    widget: '${widgetIdentifier || 'TWOJ_UNIKALNY_KOD_WIDGETU'}',
    apiBase: '${apiBase}',
    token: '${widgetIdentifier || 'TWOJ_UNIKALNY_KOD_WIDGETU'}'
  };
</script>
<script src="${widgetScriptSrc}"></script>`;

  // USUNIETE: `previewSrc`. Skladalo adres ramki podgladu, ale zadna ramka od dawna nie istnieje
  // (w tym pliku nie ma ani jednego `<iframe>`, a podglad renderuje sie Reactem obok). Wartosc nie
  // byla uzywana ANI RAZU w calym repozytorium. Przepisalem ja w pierwszej wersji tej zmiany, co
  // znaczy, ze zapowiedziane sprawdzenie po deployu „podglad ma sie wyswietlic" przeszloby nawet
  // przy calkiem blednym adresie widgetu — czyli weryfikacja niczego nie weryfikowala.
  // Pozostalosci po tamtej ramce (nasluch potwierdzenia i `postMessage` przy zapisie ustawien)
  // usuniete osobno jako P3.33, po potwierdzeniu na produkcji, ze falszywy komunikat o bledzie
  // faktycznie pojawial sie przy kazdym zapisie. Znalezione w code review PR #127.

  useEffect(() => {
    let mounted = true;
    const probe = async () => {
      // Z jawna konfiguracja sondowanie jest zbedne, a na produkcji wrecz mylace: `no-cors`
      // spelnia sie takze dla 404, wiec sonda „potwierdzala" host bez widgetu (P3.32).
      if (!czySondowac(WIDGET_BASE)) { setEmbedHost(WIDGET_BASE); setEmbedChecking(false); return; }
      setEmbedChecking(true);
      const candidates = ['http://localhost:4000', 'http://localhost:3002', apiBase].filter(Boolean);
      for (const base of candidates) {
        try {
          const baseClean = base.replace(/\/+$/, '');
          const scriptPath = base === apiBase ? '/widget/embed.js' : '/embed.js';
          await fetch(`${baseClean}${scriptPath}`, { method: 'GET', mode: 'no-cors' });
          if (!mounted) return;
          setEmbedHost(base);
          setEmbedChecking(false);
          return;
        } catch (e) {}
      }
      if (!mounted) return;
      setEmbedHost(null);
      setEmbedChecking(false);
    };
    probe();
    return () => { mounted = false; };
  }, []);

  // BYL TU `useEffect` (2026-08-xx): mierzy szerokosc PO odmalowaniu, wiec przegladarka na
  // uzytkowniku NAJPIERW rysuje podglad ze zgadywanym `synthScale=0.8` (fallback ponizej), a
  // dopiero po klatce dostaje prawdziwy pomiar i przeskalowuje `ScaledViewport` do prawdziwej
  // wysokosci — na tym ekranie widoczny jako podglad kurczacy sie o ~100px chwile po otwarciu
  // strony (zlapane, bo dokladnie ten skok zbil test E2E `widget_settings_save.cy.js`: przycisk
  // zapisu wylondowal poza widocznym oknem po tym skoku). `useLayoutEffect` mierzy PRZED
  // odmalowaniem (synchronicznie po commicie DOM), wiec przegladarka nigdy nie pokazuje —
  // ani test nie zdazy zmierzyc — zgadywanej wysokosci w ogole. Szerokosc panelu ustawien
  // nie zalezy od obrazkow ani fontow (tylko od stalej szerokosci sidebar/okna), wiec pomiar
  // przy commicie jest juz OSTATECZNY, nie tylko wczesniejszy.
  useLayoutEffect(() => {
    if (!vpRef.current) return;
    setVpWidth(vpRef.current.getBoundingClientRect().width);
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (w > 0) setVpWidth(w);
    });
    ro.observe(vpRef.current);
    return () => ro.disconnect();
  }, [loading, previewDevice]);

  const mediaMap = useMemo(() => {
    const map = {};
    media.forEach(item => { map[`${item.scope}:${normalizeKey(item.key)}`] = item.image_url || ''; });
    return map;
  }, [media]);

  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [classesRes, categoriesRes, mediaRes, settingsRes] = await Promise.all([
          fetch(`${apiBase}/api/classes`, { credentials: 'include', cache: 'no-store' }),
          fetch(`${apiBase}/api/equipment-categories`, { credentials: 'include', cache: 'no-store' }),
          fetch(`${apiBase}/api/widget-media`, { credentials: 'include' }),
          fetch(`${apiBase}/api/widget-settings`, { credentials: 'include' })
        ]);
        if (mediaRes.status === 403 || settingsRes.status === 403) {
          setError(t('widgetMedia.premiumOnly'));
          return;
        }
        const classesData = await classesRes.json().catch(() => []);
        const typesData = await categoriesRes.json().catch(() => []);
        const mediaData = await mediaRes.json().catch(() => ({}));
        const settingsData = await settingsRes.json().catch(() => ({}));
        // Od migracji 044 `/api/classes` zwraca wyłącznie słownik TEJ organizacji (wcześniej
        // przy pustym wyniku podstawiał cały słownik globalny, więc świeże konto widziało tu
        // klasy innych wypożyczalni). Pusta lista jest teraz poprawną odpowiedzią.
        setClasses(Array.isArray(classesData) ? classesData : []);
        setTypes(Array.isArray(typesData) ? typesData : []);
        setMedia(mediaData.items || []);
        setClassCombos(Array.isArray(mediaData.class_combos) ? mediaData.class_combos : []);
        if (settingsData.settings) {
          setSettings(prev => ({
            ...prev, ...settingsData.settings,
            show_images: settingsData.settings.show_images !== false,
            show_progress: settingsData.settings.show_progress !== false,
            show_size_chart: settingsData.settings.show_size_chart !== false,
            price_display_mode: settingsData.settings.price_display_mode || 'total'
          }));
        }
        setWidgetToken(settingsData.widgetToken || '');
        setWidgetAllowedOrigins(settingsData.widgetAllowedOrigins || '');
        setPlanFirmy(typeof settingsData.organizationPlan === 'string' ? settingsData.organizationPlan : null);
      } catch (e) {
        setError(t('widgetMedia.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAdmin]);

  // Podgląd karty pokazuje to, co jest zapisane (lista z serwera po każdym wgraniu i usunięciu).
  // Do 27.09.2026 były tu jeszcze szkice wpisywanych adresów: po „Usuń” pusty szkic zasłaniał
  // obrazek wgrany chwilę później i karta zostawała pusta do przeładowania strony (przegląd
  // gałęzi widget-po-przegladzie). Adresów już się nie wpisuje, więc szkice zniknęły.
  const getValue = (scope, name) => mediaMap[`${scope}:${normalizeKey(name)}`] || '';

  // Rozwinięte grupy kafelków zdjęć. Domyślnie wszystkie zwinięte; wybór pamięta przeglądarka
  // (wygoda jednej osoby, nic na serwerze; brak dostępu do pamięci = wszystkie zwinięte).
  const [otwarteGrupy, setOtwarteGrupy] = useState(() => {
    try {
      const zapis = JSON.parse(localStorage.getItem(KLUCZ_OTWARTYCH_GRUP) || '[]');
      return Array.isArray(zapis) ? zapis.filter(x => typeof x === 'string') : [];
    } catch { return []; }
  });
  const przelaczGrupe = (id) => setOtwarteGrupy(prev => {
    const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    try { localStorage.setItem(KLUCZ_OTWARTYCH_GRUP, JSON.stringify(next)); } catch { /* bez zapamiętania */ }
    return next;
  });

  const grupyKlasWKategoriach = useMemo(() => {
    const grupy = new Map();
    for (const k of classCombos) {
      if (!grupy.has(k.type_id)) grupy.set(k.type_id, { typeId: k.type_id, typeName: k.type_name, klasy: [] });
      grupy.get(k.type_id).klasy.push(k);
    }
    return [...grupy.values()];
  }, [classCombos]);

  const saveImage = async (scope, name, file = null, urlOverride = undefined) => {
    setSuccess(''); setError('');
    const key = `${scope}:${normalizeKey(name)}`;
    setSavingKey(key);
    try {
      const normalizeScopeClient = (s) => {
        const low = String(s || '').trim().toLowerCase();
        if (['class', 'classes', 'klasa', 'klasy'].includes(low)) return 'class';
        if (['category', 'categories', 'type', 'types', 'kategoria', 'kategorie'].includes(low)) return 'category';
        if (low === 'category_class') return 'category_class';
        return null;
      };
      const normalizedScope = normalizeScopeClient(scope);
      if (!normalizedScope) { setError(t('widgetMedia.invalidScope')); setTimeout(() => setError(''), 6000); setSavingKey(''); return; }
      const csrf = getCookie('csrf');
      if (file) {
        const formData = new FormData();
        formData.append('scope', normalizedScope);
        formData.append('key', normalizeKey(name));
        formData.append('file', file);
        const response = await fetch(`${apiBase}/api/widget-media/upload`, {
          method: 'POST', credentials: 'include', headers: { 'X-CSRF-Token': csrf }, body: formData
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || t('widgetMedia.uploadFailed'));
        setMedia(prev => {
          const next = prev.filter(item => !(item.scope === normalizedScope && normalizeKey(item.key) === normalizeKey(name)));
          if (data.image_url) next.push({ scope: normalizedScope, key: normalizeKey(name), image_url: data.image_url });
          return next;
        });
        setSuccess(t('widgetMedia.uploadOk'));
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const imageUrl = urlOverride !== undefined ? urlOverride : getValue(scope, name).trim();
        const response = await fetch(`${apiBase}/api/widget-media`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
          body: JSON.stringify({ scope: normalizedScope, key: normalizeKey(name), image_url: imageUrl })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || t('widgetMedia.saveFailed'));
        setMedia(prev => {
          const next = prev.filter(item => !(item.scope === normalizedScope && normalizeKey(item.key) === normalizeKey(name)));
          if (imageUrl) next.push({ scope: normalizedScope, key: normalizeKey(name), image_url: imageUrl });
          return next;
        });
        setSuccess(t('widgetMedia.savedChanges'));
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (e) { setError(e.message || t('widgetMedia.saveError')); setTimeout(() => setError(''), 6000); }
    finally { setSavingKey(''); scrollToTop(); }
  };

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const applyColorTheme = (theme) => { setCustomMode(null); setSettings(prev => ({ ...prev, primary_color: theme.primary, primary_hover: theme.hover, accent_color: theme.accent })); };

  // Tryb (jednolity/gradient) toggla własnego motywu: `null` = jeszcze nie dotknięty w tej
  // sesji, więc wyświetlany tryb jest WYWNIOSKOWANY z zapisanych danych (dwa różne kolory =
  // gradient). Po pierwszym kliknięciu przełącznika trzyma jawny wybór, żeby przejście
  // "gradient → jednolity → gradient" nie gubiło drugiego koloru w polu koloru 2.
  const [customMode, setCustomMode] = useState(null);
  const customColor1 = getSolidColor(settings.primary_color, '#2563eb');
  const customColor2 = getSolidColor(settings.primary_hover, customColor1);
  const effectiveCustomMode = customMode || (customColor1 !== customColor2 ? 'gradient' : 'solid');

  const handleCustomModeChange = (mode) => {
    setCustomMode(mode);
    if (mode === 'solid') {
      updateSetting('primary_hover', customColor1);
    } else if (mode === 'gradient' && customColor1 === customColor2) {
      // Przejście na gradient z dwoma identycznymi kolorami dawałoby podgląd bez zmiany —
      // dosuwamy drugi kolor tak, żeby efekt był od razu widoczny.
      const bumped = GRADIENT_SHUFFLE_PAIRS.find(([a]) => a.toLowerCase() === customColor1.toLowerCase());
      updateSetting('primary_hover', bumped ? bumped[1] : '#7c3aed');
    }
  };

  const handleShuffleColors = () => {
    const [a, b] = GRADIENT_SHUFFLE_PAIRS[Math.floor(Math.random() * GRADIENT_SHUFFLE_PAIRS.length)];
    setCustomMode('gradient');
    setSettings(prev => ({ ...prev, primary_color: a, primary_hover: b }));
  };
  const copyEmbedCode = async () => {
    // `navigator.clipboard.writeText` zwraca Promise, ktory ODRZUCA sie (nie rzuca
    // synchronicznie), gdy przegladarka odmowi uprawnienia do schowka (np. brak fokusu
    // na karcie, restrykcyjna polityka Safari, kontekst bez HTTPS) — bez try/catch to byl
    // NIEOBSLUZONY reject: w deweloperskim buildzie React pokazywal na tym pelnoekranowy
    // czerwony overlay „Uncaught runtime errors", a w produkcji przycisk i tak pokazywalby
    // „Skopiowano!”, mimo ze do schowka nic nie trafilo. Ten stan pokazuje sukces TYLKO
    // gdy zapis faktycznie sie powiodl, a w przeciwnym razie czytelny blad zamiast klamstwa.
    try {
      await navigator.clipboard.writeText(embedCode);
      setError('');
      setSuccess(t('widgetMedia.embedCopied'));
      setTimeout(() => setSuccess(''), 3000);
      // Baner sukcesu (setSuccess) siedzi na gorze strony, a przycisk „Kopiuj kod" jest
      // nisko w sekcji osadzania — po kliknieciu nic nie zmienialo sie TAM, gdzie akurat
      // patrzy uzytkownik, wiec kopiowanie wygladalo, jakby nic sie nie stalo. Ten stan
      // podmienia przycisk na miejscu (ptaszek + „Skopiowano!"), niezaleznie od baneru.
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (e) {
      setSuccess('');
      setError(t('widgetMedia.embedCopyFailed'));
      setTimeout(() => setError(''), 6000);
      scrollToTop();
    }
  };

  const rotateWidgetToken = async () => {
    setError(''); setSuccess(''); setRotatingToken(true);
    try {
      const csrf = getCookie('csrf');
      const response = await fetch(`${apiBase}/api/widget-settings/rotate-token`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({})
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('widgetMedia.rotateFailed'));
      setWidgetToken(data.widgetToken || '');
      setPreviewKey(prev => prev + 1);
      setSuccess(t('widgetMedia.rotateOk'));
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) { setError(e.message || t('widgetMedia.rotateError')); setTimeout(() => setError(''), 6000); }
    finally { setRotatingToken(false); scrollToTop(); }
  };

  const saveSettings = async () => {
    setSuccess(''); setError(''); setSavingSettings(true);
    try {
      const csrf = getCookie('csrf');
      const response = await fetch(`${apiBase}/api/widget-settings`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ ...settings, widget_allowed_origins: widgetAllowedOrigins })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('widgetMedia.saveSettingsFailed'));
      if (data.widgetToken) setWidgetToken(data.widgetToken);
      setWidgetAllowedOrigins(data.widgetAllowedOrigins || widgetAllowedOrigins);
      setSuccess(t('widgetMedia.settingsSaved'));
      // To JEDNO wywolanie odswiezalo podglad przez caly czas: `SyntheticWidgetPreview` jest
      // renderowany z `key={previewKey}`, wiec zmiana klucza montuje go od nowa z nowymi
      // ustawieniami, natychmiast i synchronicznie.
      //
      // P3.33: stalo tu jeszcze uzgodnienie z ramka podgladu — ustawienie flagi oczekiwania,
      // wyslanie `postMessage` do `.widget-preview-iframe` i budzik na 2,5 s, ktory przy braku
      // odpowiedzi pokazywal „Podglad nie zglosil zastosowania - wymuszono odswiezenie.".
      // Ramki nie ma, wiec odpowiedz nie miala skad przyjsc i komunikat pojawial sie przy KAZDYM
      // zapisie, mimo ze zapis sie udawal i podglad byl juz odswiezony linijke wyzej. Obsluga
      // dostawala wiec informacje o bledzie, ktorego nie bylo — a prawdziwy blad w tym miejscu
      // bylby od tego szumu nie do odroznienia. Potwierdzone na produkcji 2026-08-07.
      setPreviewKey(prev => prev + 1);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.message || t('widgetMedia.saveSettingsError'));
      setTimeout(() => setError(''), 6000);
    } finally { setSavingSettings(false); scrollToTop(); }
  };

  // `item.mediaKey` (072): klucz zdjęcia inny niż nazwa (klasa w kategorii: `<type_id>:<class_id>`),
  // `item.sub` podpis pod nazwą, `item.fallback` zdjęcie, które widget pokaże bez własnego.
  const renderRow = (scope, item) => {
    const mediaKey = item.mediaKey || item.name;
    const value = getValue(scope, mediaKey);
    const fallback = !value && item.fallback ? item.fallback : '';
    const key = `${scope}:${normalizeKey(mediaKey)}`;
    const fileInputId = `file-${key}`;
    const isBusy = savingKey === key;

    const handleFileChange = (e) => {
      const file = e.target.files?.[0];
      // Pole czyścimy od razu: inaczej pamięta wybrany plik, a przeglądarka NIE zgłasza zmiany,
      // gdy wybiera się go ponownie. Po „Usuń” wgranie tego samego zdjęcia nie robiło więc nic,
      // bez zapytania i bez komunikatu (złapane przejściem w Chromium, 27.09.2026).
      e.target.value = '';
      if (file) {
        if (file.size > 5 * 1024 * 1024) { setError(t('widgetMedia.fileTooBig')); setTimeout(() => setError(''), 6000); scrollToTop(); return; }
        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) { setError(t('widgetMedia.invalidFormat')); setTimeout(() => setError(''), 6000); scrollToTop(); return; }
        saveImage(scope, mediaKey, file);
      }
    };

    const handleDelete = async () => { await saveImage(scope, mediaKey, null, ''); };

    return (
      <MediaCard key={key} data-cy={`widget-media-card-${key}`}>
        <MediaCardPreview $fallback={!!fallback}>
          {value ? <img src={value} alt={item.name} /> : fallback ? (
            <>
              <img src={fallback} alt={item.name} />
              <FallbackBadge>{t('widgetMedia.classDefaultBadge')}</FallbackBadge>
            </>
          ) : (
            <MediaCardPlaceholder>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
              </svg>
            </MediaCardPlaceholder>
          )}
        </MediaCardPreview>
        <MediaCardInfo>
          <MediaCardName>{item.name}</MediaCardName>
          <MediaCardKey>{item.sub || normalizeKey(item.name)}</MediaCardKey>
        </MediaCardInfo>
        <MediaCardActions>
          <input type="file" id={fileInputId} accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange} style={{ display: 'none' }} />
          <UploadLabel htmlFor={fileInputId} $disabled={isBusy} title={t('widgetMedia.uploadTitle')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            {savingKey === key ? t('widgetMedia.uploading') : 'Upload'}
          </UploadLabel>
          <DeleteButton onClick={handleDelete} disabled={isBusy || !value}>{t('common.delete')}</DeleteButton>
        </MediaCardActions>
      </MediaCard>
    );
  };

  if (!isAdmin) {
    return (
      <PanelContainer>
        <PageHeader>
          <TitleRow>
            <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
              <ArrowLeftIcon />
            </IconButton>
            <div>
              <Title>{t('widgetMedia.adminOnlyTitle')}</Title>
              <Subtitle>{t('widgetMedia.adminOnlySub')}</Subtitle>
            </div>
          </TitleRow>
        </PageHeader>
      </PanelContainer>
    );
  }

  const PREVIEW_DIMS = { desktop: { w: 1280, h: 820 }, mobile: { w: 402, h: 760 } };
  const synthDims = PREVIEW_DIMS[previewDevice] || PREVIEW_DIMS.desktop;
  const synthScale = previewDevice === 'mobile'
    ? 1
    : (vpWidth > 0 ? Math.min(1, vpWidth / synthDims.w) : 0.8);
  const synthVpHeight = `${Math.round(synthDims.h * synthScale)}px`;
  const previewUrlHost = (() => {
    const first = String(widgetAllowedOrigins || '').split(/[\n,]/).map(s => s.trim()).filter(Boolean)[0];
    return (first || 'twojastrona.pl').replace(/^https?:\/\//, '').replace(/\/+$/, '');
  })();

  return (
    <PanelContainer>
      <PageHeader>
        <TitleRow>
          <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
            <ArrowLeftIcon />
          </IconButton>
          <div>
            <Title>{t('widgetMedia.title')}</Title>
            <Subtitle>{t('widgetMedia.subtitle')}</Subtitle>
          </div>
        </TitleRow>
      </PageHeader>
      <TylkoOdczytWPrzerwie>

      {error && <AlertBanner $type="error">{error}</AlertBanner>}
      {success && <AlertBanner $type="success">{success}</AlertBanner>}

      {loading ? (
        <LoadingState>{t('widgetMedia.loading')}</LoadingState>
      ) : (
        <>
          {/* Live Preview */}
          <PreviewStage>
            <PreviewBar data-tour="widget-preview">
              <PreviewBarTitle>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M8 21h8"/><path d="M12 17v4"/>
                </svg>
                {t('widgetMedia.livePreview')}
              </PreviewBarTitle>
              <PreviewBarActions>
                <DeviceToggle role="tablist" aria-label={t('widgetMedia.devicePicker')}>
                  <DeviceBtn $active={previewDevice === 'desktop'} onClick={() => setPreviewDevice('desktop')} aria-pressed={previewDevice === 'desktop'}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
                    </svg>
                    {t('widgetMedia.deviceDesktop')}
                  </DeviceBtn>
                  <DeviceBtn $active={previewDevice === 'mobile'} onClick={() => setPreviewDevice('mobile')} aria-pressed={previewDevice === 'mobile'}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="6" y="2" width="12" height="20" rx="2"/><path d="M11 18h2"/>
                    </svg>
                    {t('widgetMedia.deviceMobile')}
                  </DeviceBtn>
                </DeviceToggle>
                <RefreshButton onClick={() => setPreviewKey(prev => prev + 1)} title={t('widgetMedia.refreshPreview')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                  </svg>
                  {t('widgetMedia.refresh')}
                </RefreshButton>
              </PreviewBarActions>
            </PreviewBar>
            <PreviewBackdrop $device={previewDevice}>
              {previewDevice === 'mobile' ? (
                <PhoneFrame>
                  <PhoneNotch />
                  <PhoneScreen>
                    <ScaledViewport style={{ height: synthVpHeight }}>
                      <ScaledLayer style={{ width: `${synthDims.w}px`, transform: `scale(${synthScale})` }}>
                        <SyntheticWidgetPreview key={previewKey} settings={settings} mediaMap={mediaMap} classes={classes} types={types} device="mobile" />
                      </ScaledLayer>
                    </ScaledViewport>
                  </PhoneScreen>
                </PhoneFrame>
              ) : (
                <BrowserFrame>
                  <BrowserChrome>
                    <ChromeDots><span /><span /><span /></ChromeDots>
                    <AddressBar>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      {previewUrlHost}
                    </AddressBar>
                  </BrowserChrome>
                  <ScaledViewport ref={vpRef} style={{ height: synthVpHeight }}>
                    <ScaledLayer style={{ width: `${synthDims.w}px`, transform: `scale(${synthScale})` }}>
                      <SyntheticWidgetPreview key={previewKey} settings={settings} mediaMap={mediaMap} classes={classes} types={types} device="desktop" />
                    </ScaledLayer>
                  </ScaledViewport>
                </BrowserFrame>
              )}
            </PreviewBackdrop>
            {/* Zawsze W DOM, z ZAREZERWOWANA wysokoscia (`visibility: hidden`, nie warunkowe
                usuwanie znacznika) — sonda hosta widgetu (`czySondowac`, useEffect nizej)
                rozstrzyga sie dopiero po realnym zapytaniu sieciowym, wiec przejscie
                "sprawdzam" -> "znaleziono"/"nie znaleziono" jest NIEUNIKNIONE, ale znikniecie
                calego wiersza w tym momencie nie musi przesuwac przycisku zapisu pod nim —
                zlapane przy tym samym skoku layoutu co komentarz przy ResizeObserver wyzej. */}
            <div className="wsp-status-note" style={{ visibility: (embedChecking || !embedHost) ? 'visible' : 'hidden' }}>
              {embedChecking ? t('widgetMedia.checkingServer') : t('widgetMedia.previewNote')}
            </div>
          </PreviewStage>

          {/* Widget Settings */}
          <ContentSection>
            <SectionHeader>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6m5.196-15.196l-4.243 4.243m0 5.656l-4.242 4.243m15.196-5.196l-6-6m-6 6l-6-6m15.196 5.196l-4.243-4.243m0 5.656l-4.242-4.243"/>
              </svg>
              {t('widgetMedia.adminOnlyTitle')}
            </SectionHeader>
            <SectionSubtitle>{t('widgetMedia.settingsSub')}</SectionSubtitle>

            <SettingsGrid>
              <SettingsBlock>
                <SettingsBlockTitle>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 7h16M4 12h16M4 17h16"/>
                  </svg>
                  {t('widgetMedia.blockBasic')}
                </SettingsBlockTitle>
                <FieldGroup>
                  <FieldLabel>{t('widgetMedia.widgetName')}</FieldLabel>
                  <FieldInput type="text" value={settings.widget_title || ''} placeholder="Wypożyczalnia sprzętu" onChange={e => updateSetting('widget_title', e.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>{t('widgetMedia.allowedDomains')}</FieldLabel>
                  <FieldTextarea rows="4" value={widgetAllowedOrigins} placeholder={'Np. https://twojadomena.pl\nhttps://www.twojadomena.pl'} onChange={e => setWidgetAllowedOrigins(e.target.value)} />
                  <FieldHint>{t('widgetMedia.allowedDomainsHint')}</FieldHint>
                </FieldGroup>
                {/* S.10: link do polityki prywatności wypożyczalni pod formularzem widgetu (art. 13 RODO). */}
                <FieldGroup>
                  <FieldLabel htmlFor="widget-privacy-policy-url">{t('widgetMedia.privacyPolicyUrl')}</FieldLabel>
                  <FieldInput
                    id="widget-privacy-policy-url"
                    data-cy="widget-privacy-policy-url"
                    type="url"
                    inputMode="url"
                    value={settings.privacy_policy_url || ''}
                    placeholder={t('widgetMedia.privacyPolicyUrlPlaceholder')}
                    onChange={e => updateSetting('privacy_policy_url', e.target.value)}
                  />
                  <FieldHint>{t('widgetMedia.privacyPolicyUrlHint')}</FieldHint>
                </FieldGroup>
                {/* 074: regulamin wypożyczalni; z adresem widget pokazuje pod przyciskiem oświadczenie o akceptacji. */}
                <FieldGroup>
                  <FieldLabel htmlFor="widget-terms-url">{t('widgetMedia.termsUrl')}</FieldLabel>
                  <FieldInput
                    id="widget-terms-url"
                    data-cy="widget-terms-url"
                    type="url"
                    inputMode="url"
                    value={settings.terms_url || ''}
                    placeholder={t('widgetMedia.termsUrlPlaceholder')}
                    onChange={e => updateSetting('terms_url', e.target.value)}
                  />
                  <FieldHint>{t('widgetMedia.termsUrlHint')}</FieldHint>
                </FieldGroup>
              </SettingsBlock>

              <SettingsBlock>
                <SettingsBlockTitle>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                  </svg>
                  {t('widgetMedia.blockTheme')}
                </SettingsBlockTitle>
                <ColorThemePicker>
                  {COLOR_THEMES.map((theme, idx) => (
                    <ColorThemeOption key={idx} $active={settings.primary_color === theme.primary} onClick={() => applyColorTheme(theme)} title={t(theme.nameKey)}>
                      <ColorThemePreview style={{ background: theme.primary }} />
                      <ColorThemeName>{t(theme.nameKey)}</ColorThemeName>
                    </ColorThemeOption>
                  ))}
                </ColorThemePicker>

                {/* Presety wyżej to gotowe pary kolor+hover — widget (applyWidgetTheme,
                    widget/src/main.js) i tak sam buduje z nich `linear-gradient(135deg, …)`
                    do tła. Tu odsłaniamy TĘ SAMĄ mechanikę wprost, ale z jawnym przełącznikiem
                    trybu zamiast domyślnego domysłu z równości kolorów — dwa różne kolory dają
                    gradient, ten sam kolor w obu polach daje motyw jednolity. Żadnego nowego
                    formatu danych, żadnej zmiany w widgecie. */}
                <CustomThemeCard>
                  <CustomThemeHeader>
                    <CustomThemeTitle>{t('widgetMedia.customThemeTitle')}</CustomThemeTitle>
                    <ModeToggle role="group" aria-label={t('widgetMedia.customThemeModeAria')}>
                      <ModeToggleButton type="button" $active={effectiveCustomMode === 'solid'} onClick={() => handleCustomModeChange('solid')}>
                        {t('widgetMedia.modeSolid')}
                      </ModeToggleButton>
                      <ModeToggleButton type="button" $active={effectiveCustomMode === 'gradient'} onClick={() => handleCustomModeChange('gradient')}>
                        {t('widgetMedia.modeGradient')}
                      </ModeToggleButton>
                    </ModeToggle>
                  </CustomThemeHeader>
                  <FieldHint>{effectiveCustomMode === 'gradient' ? t('widgetMedia.customThemeHintGradient') : t('widgetMedia.customThemeHintSolid')}</FieldHint>

                  <CustomThemeBody>
                    <SwatchGroup>
                      <ColorSwatchField>
                        <FieldLabel htmlFor="custom-color-1">{effectiveCustomMode === 'gradient' ? t('widgetMedia.customColor1') : t('widgetMedia.customColorSolid')}</FieldLabel>
                        <SwatchButtonRow>
                          <ColorSwatchInput
                            id="custom-color-1"
                            type="color"
                            value={customColor1}
                            onChange={e => {
                              updateSetting('primary_color', e.target.value);
                              if (effectiveCustomMode === 'solid') updateSetting('primary_hover', e.target.value);
                            }}
                          />
                          <SwatchHex>{customColor1.toUpperCase()}</SwatchHex>
                        </SwatchButtonRow>
                      </ColorSwatchField>

                      {effectiveCustomMode === 'gradient' && (
                        <>
                          <GradientArrow aria-hidden="true">→</GradientArrow>
                          <ColorSwatchField>
                            <FieldLabel htmlFor="custom-color-2">{t('widgetMedia.customColor2')}</FieldLabel>
                            <SwatchButtonRow>
                              <ColorSwatchInput
                                id="custom-color-2"
                                type="color"
                                value={customColor2}
                                onChange={e => updateSetting('primary_hover', e.target.value)}
                              />
                              <SwatchHex>{customColor2.toUpperCase()}</SwatchHex>
                            </SwatchButtonRow>
                          </ColorSwatchField>
                          <ShuffleButton type="button" onClick={handleShuffleColors} title={t('widgetMedia.shuffleColors')} aria-label={t('widgetMedia.shuffleColors')}>
                            <ShuffleIconSvg />
                          </ShuffleButton>
                        </>
                      )}

                      <ColorSwatchField style={{ marginLeft: 'auto' }}>
                        <FieldLabel htmlFor="custom-color-accent">{t('widgetMedia.customAccent')}</FieldLabel>
                        <SwatchButtonRow>
                          <ColorSwatchInput
                            id="custom-color-accent"
                            type="color"
                            value={getSolidColor(settings.accent_color, '#10b981')}
                            onChange={e => updateSetting('accent_color', e.target.value)}
                          />
                        </SwatchButtonRow>
                      </ColorSwatchField>
                    </SwatchGroup>

                    <MiniPreview>
                      <MiniPreviewHeader style={{ background: effectiveCustomMode === 'gradient' ? `linear-gradient(135deg, ${customColor1}, ${customColor2})` : customColor1 }}>
                        {settings.widget_title || t('widgetMedia.previewFallbackTitle')}
                      </MiniPreviewHeader>
                      <MiniPreviewBody>
                        <MiniPreviewLine />
                        <MiniPreviewLine $short />
                        <MiniPreviewCta style={{ background: getSolidColor(settings.accent_color, '#10b981') }}>
                          {t('widgetMedia.previewCta')}
                        </MiniPreviewCta>
                      </MiniPreviewBody>
                    </MiniPreview>
                  </CustomThemeBody>
                </CustomThemeCard>
              </SettingsBlock>

              <SettingsBlock>
                <SettingsBlockTitle>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M9 3v18M3 9h18M3 15h18"/>
                  </svg>
                  {t('widgetMedia.blockPresentation')}
                </SettingsBlockTitle>
                <FieldGroup>
                  <FieldLabel>{t('widgetMedia.priceMode')}</FieldLabel>
                  <div style={{ width: '100%' }}>
                    <DiscountUnitDropdown
                      value={settings.price_display_mode || 'total'}
                      onChange={v => updateSetting('price_display_mode', v)}
                      options={[{ value: 'total', label: t('widgetMedia.priceTotal') }, { value: 'per_day', label: t('widgetMedia.priceDaily') }]}
                      fullWidth={true}
                    />
                  </div>
                </FieldGroup>
                <TogglesGrid>
                  {[
                    { key: 'show_images', label: t('widgetMedia.showImages'), icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg> },
                    { key: 'show_progress', label: t('widgetMedia.showProgress'), icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg> },
                    { key: 'show_size_chart', label: t('widgetMedia.showSizeChart'), icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg> },
                  ].map(t => (
                    <CheckboxRow key={t.key} as="label">
                      <input type="checkbox" checked={settings[t.key] !== false} onChange={e => updateSetting(t.key, e.target.checked)} style={{ accentColor: 'var(--primary)', width: 18, height: 18, cursor: 'pointer' }} />
                      <CheckboxText>{t.icon}{t.label}</CheckboxText>
                    </CheckboxRow>
                  ))}
                </TogglesGrid>
              </SettingsBlock>
            </SettingsGrid>

            <SettingsActions>
              <PrimaryButton data-cy="widget-settings-save" onClick={saveSettings} disabled={savingSettings}>
                {savingSettings ? t('common.saving') : t('widgetMedia.saveSettings')}
              </PrimaryButton>
            </SettingsActions>
          </ContentSection>

          {/* Filtry w widgecie (068): cechy sprzętu, po których klient zawęża listę */}
          <ContentSection>
            <SectionHeader>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              {t('widgetFilters.title')}
            </SectionHeader>
            <SectionSubtitle>{t('widgetFilters.sub')}</SectionSubtitle>
            <WidgetFiltersPanel />
          </ContentSection>

          {/* Rezerwacja według klas (069): klient wybiera klasę i podaje wymiary, firma dobiera sprzęt */}
          <ContentSection>
            <SectionHeader>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18"/><path d="M7 15l4-4 3 3 5-6"/>
              </svg>
              {t('classBooking.title')}
            </SectionHeader>
            <SectionSubtitle>{t('classBooking.sub')}</SectionSubtitle>
            <WidgetClassBookingPanel />
          </ContentSection>

          {/* Klasy sprzętu */}
          <ContentSection>
            <SectionHeader>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
              </svg>
              {t('widgetMedia.sectionClasses')}
            </SectionHeader>
            <SectionSubtitle>{t('widgetMedia.imagesClassesHint')} <strong>800×600px</strong>{t('widgetMedia.imagesFormatHint')}</SectionSubtitle>
            <ZwijanaGrupa
              id="classes-default"
              tytul={t('widgetMedia.classesDefaultGroup')}
              opis={t('widgetMedia.classesDefaultHint')}
              wgrane={classes.filter(c => getValue('class', c.name)).length}
              wszystkie={classes.length}
              otwarta={otwarteGrupy.includes('classes-default')}
              onToggle={() => przelaczGrupe('classes-default')}
            >
              <MediaGrid>{classes.map(item => renderRow('class', item))}</MediaGrid>
            </ZwijanaGrupa>
            {/* 072: zdjęcie klasy osobno w każdej kategorii (np. inne VIP dla nart i dla butów) */}
            {grupyKlasWKategoriach.map(grupa => {
              const id = `category-classes-${grupa.typeId}`;
              return (
                <ZwijanaGrupa
                  key={grupa.typeId}
                  id={id}
                  tytul={grupa.typeName}
                  wgrane={grupa.klasy.filter(k => getValue('category_class', `${k.type_id}:${k.class_id}`)).length}
                  wszystkie={grupa.klasy.length}
                  otwarta={otwarteGrupy.includes(id)}
                  onToggle={() => przelaczGrupe(id)}
                >
                  <MediaGrid>
                    {grupa.klasy.map(k => renderRow('category_class', {
                      name: k.class_name,
                      mediaKey: `${k.type_id}:${k.class_id}`,
                      sub: grupa.typeName,
                      fallback: getValue('class', k.class_name),
                    }))}
                  </MediaGrid>
                </ZwijanaGrupa>
              );
            })}
          </ContentSection>

          {/* Kategorie */}
          <ContentSection>
            <SectionHeader>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              {t('widgetMedia.sectionCategories')}
            </SectionHeader>
            <SectionSubtitle>{t('widgetMedia.imagesCategoriesHint')} <strong>800×600px</strong>{t('widgetMedia.imagesFormatHint')}</SectionSubtitle>
            <ZwijanaGrupa
              id="categories"
              tytul={t('widgetMedia.categoriesGroup')}
              wgrane={types.filter(c => getValue('category', c.name)).length}
              wszystkie={types.length}
              otwarta={otwarteGrupy.includes('categories')}
              onToggle={() => przelaczGrupe('categories')}
            >
              <MediaGrid>{types.map(item => renderRow('category', item))}</MediaGrid>
            </ZwijanaGrupa>
          </ContentSection>

          {superAdminBezPremium && (
            <Banner tone="warning" style={{ marginBottom: 24 }} data-cy="widget-superadmin-plan">
              {t('widgetMedia.superAdminNotPremium')}
            </Banner>
          )}

          <BookingLinkQr
            link={linkDoRezerwacji}
            isDemo={isDemo}
            orgName={settings.org_name || localStorage.getItem('organizationName') || ''}
          />

          {/* Embed Code */}
          <EmbedSection>
            <EmbedSectionInner>
              <EmbedHeader data-tour="widget-embed">
                <div>
                  <EmbedTitle>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                    {t('widgetMedia.embedTitle')}
                  </EmbedTitle>
                  <EmbedSubtitle>{t('widgetMedia.embedSubtitle')}</EmbedSubtitle>
                  {isDemo && (
                    <EmbedDemoNote data-cy="widget-embed-demo-note">{t('widgetMedia.embedDemoNote')}</EmbedDemoNote>
                  )}
                </div>
                <EmbedToggleButton data-cy="widget-embed-toggle" onClick={() => setShowEmbedCode(!showEmbedCode)}>
                  {showEmbedCode ? t('widgetMedia.hideCode') : t('widgetMedia.showCode')}
                </EmbedToggleButton>
              </EmbedHeader>
              {showEmbedCode && (
                <EmbedBox ref={embedBoxRef}>
                  <pre><code data-cy="widget-embed-code">{embedCode}</code></pre>
                  <CopyButton data-cy="widget-embed-copy" onClick={copyEmbedCode} $copied={codeCopied}>
                    {codeCopied ? (
                      <>
                        <CheckIcon size={16} />
                        {t('widgetMedia.copied')}
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                        {t('widgetMedia.copyCode')}
                      </>
                    )}
                  </CopyButton>
                </EmbedBox>
              )}
            </EmbedSectionInner>
          </EmbedSection>
        </>
      )}
      </TylkoOdczytWPrzerwie>
    </PanelContainer>
  );
}

export default WidgetMedia;

// ── Styled Components ─────────────────────────────────────────────────────────

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AlertBanner = styled.div`
  padding: 14px 18px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  animation: ${slideDown} 0.3s ease-out;
  line-height: 1.5;

  ${p => p.$type === 'error' && `
    background: var(--danger-bg);
    border: 1px solid var(--danger-border);
    color: var(--danger);
    &::before { content: "⚠ "; }
  `}
  ${p => p.$type === 'success' && `
    background: var(--success-bg);
    border: 1px solid var(--success-border);
    color: var(--success);
    &::before { content: "✓ "; }
  `}
`;

const LoadingState = styled.div`
  padding: 60px 20px;
  color: var(--text-muted);
  text-align: center;
  font-size: 1rem;
`;

const PreviewStage = styled.section`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 8px 28px var(--shadow-color);
  margin-bottom: 24px;
`;

const PreviewBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 13px 18px;
  border-bottom: 1px solid var(--input-border);
  background: var(--table-header-bg);
`;

const PreviewBarTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg { color: var(--primary); }
`;

const PreviewBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const DeviceToggle = styled.div`
  display: inline-flex;
  background: var(--panel-bg);
  border: 1.5px solid var(--input-border);
  border-radius: 11px;
  padding: 3px;
  gap: 2px;
`;

const DeviceBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 13px;
  border: none;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.18s ease;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? 'var(--primary-contrast)' : 'var(--text-muted)'};
  box-shadow: ${p => p.$active ? '0 2px 8px rgba(59,130,246,0.35)' : 'none'};

  &:hover { color: ${p => p.$active ? 'var(--primary-contrast)' : 'var(--text)'}; }
  svg { width: 15px; height: 15px; }
`;

const RefreshButton = styled.button`
  padding: 7px 14px;
  border: 1.5px solid var(--input-border);
  background: var(--card-bg);
  color: var(--text-muted);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: inherit;

  &:hover:not(:disabled) { background: var(--table-row-hover); color: var(--primary); border-color: var(--primary); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PreviewBackdrop = styled.div`
  display: flex;
  justify-content: center;
  padding: ${p => p.$device === 'mobile' ? '32px 20px' : '24px'};
  background:
    radial-gradient(circle at 18% 0%, rgba(59,130,246,0.07), transparent 42%),
    radial-gradient(circle at 100% 100%, rgba(139,92,246,0.07), transparent 46%),
    repeating-linear-gradient(45deg, transparent 0 11px, rgba(120,130,150,0.045) 11px 12px),
    var(--panel-bg);
`;

// Makieta przeglądarki/telefonu podglądu widgetu (do BrowserFrame/PhoneFrame/PhoneScreen) —
// celowo NIEZALEŻNA od motywu panelu: ma wyglądać jak prawdziwa, jasna przeglądarka klienta,
// niezależnie od tego, czy admin ogląda ten podgląd w trybie ciemnym.
const ScaledViewport = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  background: #f8f9fa;
`;

const ScaledLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
`;

/* Desktop browser chrome */
const BrowserFrame = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15,23,42,0.08);
  box-shadow: 0 18px 50px rgba(15,23,42,0.20), 0 4px 12px rgba(15,23,42,0.08);
`;

const BrowserChrome = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  background: #e7eaf0;
  border-bottom: 1px solid rgba(15,23,42,0.08);
`;

const ChromeDots = styled.div`
  display: flex;
  gap: 7px;
  flex-shrink: 0;

  span { width: 12px; height: 12px; border-radius: 50%; display: block; }
  span:nth-child(1) { background: #ff5f57; }
  span:nth-child(2) { background: #febc2e; }
  span:nth-child(3) { background: #28c840; }
`;

const AddressBar = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 12px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid rgba(15,23,42,0.10);
  color: #64748b;
  font-size: 0.78rem;
  font-family: 'SF Mono','Fira Code','Courier New',monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  svg { flex-shrink: 0; color: #16a34a; }
`;

/* Mobile phone frame */
const PhoneFrame = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: 12px;
  background: #0b1220;
  border-radius: 46px;
  box-shadow: 0 26px 60px rgba(15,23,42,0.35), inset 0 0 0 2px rgba(255,255,255,0.06);
`;

const PhoneScreen = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 32px;
  background: #f8f9fa;
  width: 402px;
`;

const PhoneNotch = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 148px;
  height: 26px;
  background: #0b1220;
  border-radius: 0 0 16px 16px;
  z-index: 5;
`;

// Embed section
const EmbedSection = styled.div`
  margin-bottom: 24px;
`;

const EmbedSectionInner = styled.div`
  background: #312e81;
  padding: 28px;
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(167,139,250,0.15) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const EmbedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
  gap: 16px;
  flex-wrap: wrap;
`;

const EmbedTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: -0.01em;

  svg { flex-shrink: 0; opacity: 0.9; }
`;

const EmbedSubtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: rgba(255,255,255,0.75);
  line-height: 1.5;
`;

const EmbedDemoNote = styled.p`
  margin: 10px 0 0 0;
  padding: 10px 12px;
  font-size: 0.875rem;
  line-height: 1.5;
  color: white;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 10px;
`;

const EmbedToggleButton = styled.button`
  padding: 10px 20px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
  font-family: inherit;
  white-space: nowrap;

  &:hover { background: rgba(255,255,255,0.22); transform: translateY(-1px); }
`;

const EmbedBox = styled.div`
  position: relative;
  background: #0f172a;
  border-radius: 12px;
  padding: 22px;
  margin-top: 16px;
  border: 1px solid rgba(255,255,255,0.08);
  z-index: 1;

  pre { margin: 0; overflow-x: auto; font-family: 'SF Mono','Fira Code','Courier New',monospace; font-size: 0.84rem; line-height: 1.7; }
  code { color: #a5f3fc; }
`;

const CopyButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  padding: 8px 16px;
  background: ${p => p.$copied ? 'rgba(34,197,94,0.85)' : 'rgba(255,255,255,0.1)'};
  border: 1px solid ${p => p.$copied ? 'rgba(34,197,94,0.9)' : 'rgba(255,255,255,0.15)'};
  color: white;
  border-radius: 9px;
  cursor: pointer;
  font-size: 0.83rem;
  font-weight: 600;
  transition: all 0.2s;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;

  &:hover { background: ${p => p.$copied ? 'rgba(34,197,94,0.85)' : 'rgba(255,255,255,0.2)'}; transform: translateY(-1px); }
`;

// Content sections
const ContentSection = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px var(--shadow-color);
`;

const SectionHeader = styled.h2`
  margin: 0 0 6px 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: -0.01em;

  svg { color: var(--primary); flex-shrink: 0; }
`;

const SectionSubtitle = styled.p`
  color: var(--text-muted);
  margin: 0 0 20px 0;
  font-size: 0.9rem;
  line-height: 1.6;

  strong { color: var(--text); }
`;

const SettingsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;

const SettingsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: var(--panel-bg);
  border-radius: 12px;
  border: 1px solid var(--input-border);
  transition: border-color 0.2s;

  &:hover { border-color: var(--primary); }
`;

const SettingsBlockTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: -0.01em;

  svg { color: var(--primary); flex-shrink: 0; }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text);
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 0.9rem;
  background: var(--input-bg);
  color: var(--text);
  transition: all 0.2s;
  font-family: inherit;
  box-sizing: border-box;

  &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
  &::placeholder { color: var(--text-muted); opacity: 0.7; }
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 0.9rem;
  background: var(--input-bg);
  color: var(--text);
  transition: all 0.2s;
  font-family: inherit;
  resize: vertical;
  min-height: 96px;
  line-height: 1.5;
  box-sizing: border-box;

  &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
`;

const FieldHint = styled.small`
  color: var(--text-muted);
  font-size: 0.8rem;
  line-height: 1.5;
`;

const TogglesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
`;

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1.5px solid var(--input-border);
  border-radius: 10px;
  background: var(--card-bg);
  cursor: pointer;
  transition: all 0.2s;

  &:hover { border-color: var(--primary); background: var(--table-row-hover); }
`;

const CheckboxText = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--text);
  font-weight: 500;

  svg { color: var(--primary); flex-shrink: 0; opacity: 0.8; }
`;

const SettingsActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
`;

// Media cards
const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const MediaCard = styled.div`
  display: flex;
  flex-direction: column;
  border: 1.5px solid var(--input-border);
  border-radius: 14px;
  background: var(--panel-bg);
  overflow: hidden;
  transition: all 0.25s ease;

  &:hover {
    border-color: var(--primary);
    box-shadow: 0 8px 24px var(--shadow-color);
    transform: translateY(-3px);
  }
`;

const MediaCardPreview = styled.div`
  width: 100%;
  aspect-ratio: 16/10;
  overflow: hidden;
  background: var(--table-header-bg);
  display: flex;
  align-items: center;
  justify-content: center;

  position: relative;

  img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s ease; }
  ${MediaCard}:hover img { transform: scale(1.04); }
  ${(p) => p.$fallback && 'img { opacity: 0.45; }'}
`;

const MediaCardPlaceholder = styled.div`
  color: var(--input-border);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0.5;
`;

const MediaCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px 6px;
`;

const MediaGroup = styled.div`
  border-top: 1px solid var(--input-border);
  ${SectionSubtitle} + & { border-top: none; }
`;

const GroupToggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 8px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--text);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover { background: var(--table-header-bg); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const GroupChevron = styled.span`
  flex: none;
  display: inline-flex;
  color: var(--text-muted);
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.$open ? '90deg' : '0deg')});
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const GroupText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GroupCount = styled.span`
  flex: none;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
`;

const GroupBody = styled.div`
  padding: 4px 0 20px;
`;

const MediaGroupTitle = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
`;

const MediaGroupHint = styled.span`
  font-size: 0.85rem;
  color: var(--text-muted);
`;

// Zwijana grupa kafelków zdjęć (28.10.2026): po rozwinięciu kafelki jak dotąd, zwinięta zajmuje
// jeden wiersz z nazwą i liczbą wgranych zdjęć. Przy wielu kategoriach sekcja zdjęć klas
// zajmowała kilka ekranów.
function ZwijanaGrupa({ id, tytul, opis, wgrane, wszystkie, otwarta, onToggle, children }) {
  const { t } = useTranslation();
  const bodyId = `widget-media-group-${id}-body`;
  return (
    <MediaGroup data-cy={`widget-media-group-${id}`}>
      <GroupToggle type="button" aria-expanded={otwarta} aria-controls={otwarta ? bodyId : undefined} onClick={onToggle} data-cy={`widget-media-group-toggle-${id}`}>
        <GroupChevron $open={otwarta} aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18"/></svg>
        </GroupChevron>
        <GroupText>
          <MediaGroupTitle>{tytul}</MediaGroupTitle>
          {opis && <MediaGroupHint>{opis}</MediaGroupHint>}
        </GroupText>
        <GroupCount>{t('widgetMedia.groupImages', { own: wgrane, all: wszystkie })}</GroupCount>
      </GroupToggle>
      {otwarta && <GroupBody id={bodyId}>{children}</GroupBody>}
    </MediaGroup>
  );
}

// Zdjęcie domyślne klasy na kafelku klasy w kategorii: przygaszone, z podpisem, żeby było
// widać, że to nie własne zdjęcie tej kategorii.
const FallbackBadge = styled.span`
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
`;

const MediaCardName = styled.div`
  font-weight: 700;
  color: var(--text);
  font-size: 0.9rem;
  letter-spacing: -0.01em;
`;

const MediaCardKey = styled.div`
  color: var(--text-muted);
  font-size: 0.72rem;
  font-family: 'SF Mono','Fira Code','Courier New',monospace;
  background: var(--table-header-bg);
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
  width: fit-content;
`;

const MediaCardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 14px 14px;
`;

const UploadLabel = styled.label`
  width: 100%;
  padding: 9px 14px;
  border-radius: 10px;
  background: var(--primary);
  color: white;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
  opacity: ${p => p.$disabled ? 0.5 : 1};
  box-sizing: border-box;

  &:hover { ${p => !p.$disabled && 'transform: translateY(-1px); box-shadow: var(--shadow-primary);'} }
`;

const DeleteButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1.5px solid var(--danger-border);
  background: var(--danger-bg);
  color: var(--danger);
  cursor: pointer;
  font-size: 0.83rem;
  font-weight: 600;
  transition: all 0.2s;
  font-family: inherit;

  &:hover:not(:disabled) { background: color-mix(in srgb, var(--danger) 12%, transparent); border-color: var(--danger); transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// Color theme picker
const ColorThemePicker = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 4px;
`;

const ColorThemeOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  border: 2px solid ${p => p.$active ? 'var(--primary)' : 'var(--input-border)'};
  border-radius: 12px;
  background: ${p => p.$active ? 'var(--table-row-hover)' : 'var(--card-bg)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  &:hover { border-color: var(--primary); transform: translateY(-2px); box-shadow: 0 4px 12px var(--shadow-color); }
  ${p => p.$active && 'box-shadow: 0 4px 16px var(--shadow-color);'}
`;

const ColorThemePreview = styled.div`
  width: 100%;
  height: 40px;
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
`;

const ColorThemeName = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-muted);
  text-align: center;
  line-height: 1.3;
`;

const CustomThemeCard = styled.div`
  margin-top: 16px;
  padding: 20px;
  border: 1px solid var(--input-border);
  border-radius: 14px;
  background: var(--input-bg);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CustomThemeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CustomThemeTitle = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
`;

// Przełącznik jednolity/gradient — ten sam wzorzec segmentowanej pigułki co WeekdayChip
// (PriceRules.js), tylko dwuopcyjny.
const ModeToggle = styled.div`
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 999px;
`;

const ModeToggleButton = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background .15s, color .15s;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? 'var(--primary-contrast)' : 'var(--text-muted)'};

  &:hover { color: ${p => p.$active ? 'var(--primary-contrast)' : 'var(--text)'}; }
`;

const CustomThemeBody = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const SwatchGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 260px;
`;

const ColorSwatchField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SwatchButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Natywny <input type="color"> — nie da się przemalować środka (to systemowy widget
// przeglądarki), więc stylujemy tylko ramkę/rozmiar, spójnie z resztą pól formularza.
const ColorSwatchInput = styled.input`
  width: 48px;
  height: 48px;
  padding: 3px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  background: var(--card-bg);
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: transform .15s, border-color .15s;

  &:hover { transform: translateY(-1px); border-color: var(--primary); }
  &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
`;

const SwatchHex = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.75rem;
  color: var(--text-muted);
  letter-spacing: 0.02em;
`;

const GradientArrow = styled.span`
  color: var(--text-muted);
  font-size: 1.1rem;
  padding-bottom: 12px;
`;

const ShuffleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 2px;
  border: 1px solid var(--input-border);
  border-radius: 10px;
  background: var(--card-bg);
  color: var(--text-muted);
  cursor: pointer;
  transition: color .15s, border-color .15s, transform .15s;

  &:hover { color: var(--primary); border-color: var(--primary); transform: rotate(20deg); }
`;

// Podgląd nie jest płaskim prostokątem koloru — to miniaturowa makieta realnego widgetu
// (pasek z tytułem + przycisk CTA w kolorze akcentu), żeby admin widział, JAK to wygląda
// u klienta, a nie tylko jaki to kolor.
const MiniPreview = styled.div`
  width: 220px;
  flex-shrink: 0;
  border: 1px solid var(--input-border);
  border-radius: 14px;
  overflow: hidden;
  background: var(--card-bg);
  box-shadow: var(--shadow-sm);
`;

const MiniPreviewHeader = styled.div`
  padding: 14px 16px;
  color: white;
  font-weight: 700;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MiniPreviewBody = styled.div`
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MiniPreviewLine = styled.div`
  height: 7px;
  border-radius: 4px;
  background: var(--input-border);
  width: ${p => p.$short ? '55%' : '85%'};
`;

const MiniPreviewCta = styled.div`
  margin-top: 6px;
  align-self: flex-start;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
`;

const ShuffleIconSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 3 21 3 21 8" />
    <line x1="4" y1="20" x2="21" y2="3" />
    <polyline points="21 16 21 21 16 21" />
    <line x1="15" y1="15" x2="21" y2="21" />
    <line x1="4" y1="4" x2="9" y2="9" />
  </svg>
);
